import React, { useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Popover } from './Popover';
import ObliqueIcon from '@/components/icons/ObliqueIcon';

export function MultiSelect({ 
    options = [], 
    value = [], // Array of option ids
    onChange, 
    placeholder = 'Select options...',
    className = '' 
}) {
    const handleToggle = (optionId) => {
        if (value.includes(optionId)) {
            onChange(value.filter(id => id !== optionId));
        } else {
            onChange([...value, optionId]);
        }
    };

    const handleRemove = (e, optionId) => {
        e.stopPropagation();
        onChange(value.filter(id => id !== optionId));
    };

    const selectedOptions = useMemo(() => {
        return value.map(id => options.find(o => o.id === id)).filter(Boolean);
    }, [value, options]);

    const trigger = (
        <div className={`
            min-h-[40px] px-3 py-1.5 rounded-lg border border-[var(--border)]
            bg-[var(--bg-surface)] hover:border-[var(--border-strong)] transition-colors
            flex flex-wrap gap-1.5 items-center w-full min-w-[250px]
            ${className}
        `}>
            {selectedOptions.length === 0 ? (
                <span className="text-[var(--text-tertiary)] py-1">{placeholder}</span>
            ) : (
                <AnimatePresence>
                    {selectedOptions.map(opt => (
                        <motion.div
                            key={opt.id}
                            layout
                            initial={{ opacity: 0, scale: 0.8 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0, scale: 0.8 }}
                            transition={{ type: 'spring', stiffness: 500, damping: 30 }}
                            className="flex items-center gap-1 bg-[var(--bg-subtle)] border border-[var(--border)] text-[var(--text-primary)] px-2 py-0.5 rounded-md text-sm"
                        >
                            <span>{opt.label}</span>
                            <button 
                                onClick={(e) => handleRemove(e, opt.id)}
                                className="opacity-60 hover:opacity-100 outline-none rounded-sm"
                            >
                                <ObliqueIcon name="close" size={12} />
                            </button>
                        </motion.div>
                    ))}
                </AnimatePresence>
            )}
            <div className="ml-auto text-[var(--text-tertiary)] opacity-70">
                <ObliqueIcon name="expand_more" size={18} />
            </div>
        </div>
    );

    const content = (
        <div className="flex flex-col gap-1 max-h-[300px] overflow-y-auto min-w-[250px]">
            {options.map(opt => {
                const isSelected = value.includes(opt.id);
                return (
                    <div 
                        key={opt.id}
                        onClick={() => handleToggle(opt.id)}
                        className={`
                            flex items-center gap-2 px-3 py-2 rounded-md cursor-pointer transition-colors
                            ${isSelected ? 'bg-[var(--bg-subtle)] text-[var(--text-primary)] font-medium' : 'hover:bg-[var(--bg-subtle)] text-[var(--text-secondary)]'}
                        `}
                    >
                        <div className={`
                            w-4 h-4 rounded border flex items-center justify-center shrink-0 transition-colors
                            ${isSelected ? 'bg-[var(--accent)] border-[var(--accent)] text-white' : 'border-[var(--border)] bg-transparent'}
                        `}>
                            {isSelected && <ObliqueIcon name="check" size={12} />}
                        </div>
                        <span className="text-sm truncate">{opt.label}</span>
                    </div>
                );
            })}
        </div>
    );

    return (
        <Popover trigger={trigger} content={content} position="bottom" />
    );
}
